import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, AlertTriangle, Heart, FileText, CheckCircle, RotateCcw } from "lucide-react";

const REASON_OPTIONS = [
  { value: "alerji", label: "Alerji" },
  { value: "uyumsuzluk", label: "Aile/Köpek Uyumsuzluğu" },
  { value: "davranis", label: "Davranış Sorunu" },
  { value: "tasinma", label: "Taşınma / Yaşam Değişimi" },
  { value: "saglik", label: "Sağlık Durumu" },
  { value: "finansal", label: "Finansal Zorluk" },
  { value: "vefat_sahip", label: "Sahibin Vefatı" },
  { value: "diger", label: "Diğer" },
];

const reasonLabel = (v) => REASON_OPTIONS.find((r) => r.value === v)?.label || v;

export default function AdoptionReturnsPanel() {
  const [returns, setReturns] = useState([]);
  const [adopted, setAdopted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ dog_id: "", reason: "", notes: "", return_date: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [retRes, dogsRes] = await Promise.all([
        axios.get(`${API}/admin/adoption-returns`),
        axios.get(`${API}/dogs?status=sahiplendirildi&limit=200`),
      ]);
      setReturns(retRes.data.returns || []);
      setAdopted(dogsRes.data.dogs || []);
    } catch {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!form.dog_id || !form.reason) {
      toast.error("Köpek ve sebep zorunlu");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/adoption-returns`, form);
      toast.success("İade kaydedildi · köpek statüsü 'sahiplendirmeye hazır'a döndürüldü");
      setOpen(false);
      setForm({ dog_id: "", reason: "", notes: "", return_date: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="adoption-returns-panel">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Sahiplendirme İadeleri</h1>
          <p className="text-muted-foreground mt-1 max-w-xl">
            Şeffaflık raporunun en kritik outcome metriği. İade kaydedildiğinde köpek otomatik olarak
            yeniden "sahiplendirmeye hazır" duruma alınır.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill bg-primary text-primary-foreground" data-testid="open-return-form">
              <Plus className="w-4 h-4 mr-2" />
              Yeni İade Kayıtla
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-md">
            <DialogHeader>
              <DialogTitle>Sahiplendirme İadesi Kaydet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Köpek (sahiplendirilmiş)</Label>
                <Select value={form.dog_id} onValueChange={(v) => setForm({ ...form, dog_id: v })}>
                  <SelectTrigger data-testid="return-dog-select">
                    <SelectValue placeholder={adopted.length === 0 ? "Sahiplendirilmiş köpek yok" : "Seç"} />
                  </SelectTrigger>
                  <SelectContent>
                    {adopted.map((d) => (
                      <SelectItem key={d.dog_id} value={d.dog_id}>
                        {d.name} · {d.dog_id.slice(-6)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sebep</Label>
                <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seç" />
                  </SelectTrigger>
                  <SelectContent>
                    {REASON_OPTIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>İade Tarihi (boşsa bugün)</Label>
                <Input type="date" value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} />
              </div>
              <div>
                <Label>Notlar (gizli — public sayfada görünmez)</Label>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Detaylar, takip yapılacaklar..."
                />
              </div>
              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <span>
                  Bu kayıt köpeği yeniden "sahiplendirmeye hazır" yapar ve şeffaflık raporundaki
                  retention oranını etkiler. Yalnız iade gerçekten olduysa kullan.
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
                <Button onClick={submit} disabled={saving} data-testid="submit-return">
                  {saving ? "Kaydediliyor…" : "İadeyi Kaydet"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard icon={RotateCcw} value={returns.length} label="Toplam İade" color="rose" />
        <StatCard icon={Heart} value={adopted.length} label="Şu an sahiplendirilmiş" color="emerald" />
        <StatCard
          icon={CheckCircle}
          value={
            adopted.length + returns.length === 0
              ? "—"
              : `${Math.round(((adopted.length) / (adopted.length + returns.length)) * 100)}%`
          }
          label="Tutma oranı"
          color="amber"
        />
      </div>

      <div>
        <h2 className="font-heading text-xl mb-4">İade Geçmişi</h2>
        {loading ? (
          <p className="text-muted-foreground">Yükleniyor…</p>
        ) : returns.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-10 text-center text-muted-foreground">
              Henüz iade kaydı yok. Bu iyi haber — tüm sahiplendirmeler kalıcı.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {returns.map((r) => (
              <Card key={r.return_id} className="bg-card border border-border/50">
                <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-heading text-lg">{r.dog_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.return_date).toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {reasonLabel(r.reason)}
                  </Badge>
                  {r.notes && (
                    <p className="text-xs text-muted-foreground italic flex-1 min-w-[150px]">"{r.notes}"</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color }) {
  const colors = {
    rose: "bg-rose-50 text-rose-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <Card className="bg-card border border-border/50">
      <CardContent className="p-5">
        <div className={`w-10 h-10 rounded-xl ${colors[color]} flex items-center justify-center mb-3`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <p className="font-heading text-3xl">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </CardContent>
    </Card>
  );
}
